'use client';

import { useEffect, useState, type KeyboardEvent } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import useEmblaCarousel from 'embla-carousel-react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';

import { playImageReveal, selectedSlideImage } from '@/lib/image-reveal';
import { mergeClassNames } from '@/lib/utils';
import { RevealImage } from './reveal-image';

type LightboxImage = { id: string; url: string };

// aria-disabled rather than disabled: a disabled button drops its focus to the
// page behind the viewer, and Embla already stops at either end.
const NAV_BUTTON =
  'mb-focus-visible absolute top-1/2 hidden size-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 aria-disabled:cursor-default aria-disabled:opacity-30 aria-disabled:hover:bg-white/10 sm:flex';

export const ImageLightbox = ({
  images,
  alt,
  startIndex,
  onClose,
}: {
  images: LightboxImage[];
  alt: string;
  startIndex: number;
  onClose: () => void;
}) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false, startIndex });
  const [index, setIndex] = useState(startIndex);
  // The viewer opens with no Dialog.Trigger, so Radix has nothing to give focus
  // back to on close; the photo that opened it takes it instead.
  const [opener] = useState(() =>
    document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null
  );
  const hasMany = images.length > 1;

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setIndex(emblaApi.selectedScrollSnap());
    emblaApi.on('select', onSelect);
    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi]);

  // An arrow key jumps to the photo and brings it into focus, as a gallery
  // thumbnail does; a swipe and the buttons still slide.
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!emblaApi) return;
    if (event.key === 'ArrowLeft' && emblaApi.canScrollPrev()) {
      emblaApi.scrollPrev(true);
    } else if (event.key === 'ArrowRight' && emblaApi.canScrollNext()) {
      emblaApi.scrollNext(true);
    } else return;
    playImageReveal(selectedSlideImage(emblaApi));
  };

  return (
    <Dialog.Root open onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[70] bg-black/95" />
        <Dialog.Content
          aria-describedby={undefined}
          onKeyDown={handleKeyDown}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            opener?.focus();
          }}
          className="fixed inset-0 z-[70] flex flex-col outline-none [--mb-focus-color:white]"
        >
          <Dialog.Title className="sr-only">{alt}</Dialog.Title>
          <div className="flex items-center justify-between px-4 pb-2 pt-[max(0.75rem,env(safe-area-inset-top))]">
            <span
              aria-live="polite"
              className="text-sm font-medium text-white/80"
            >
              {hasMany ? `${index + 1} / ${images.length}` : ''}
            </span>
            <Dialog.Close
              aria-label="Close"
              className="mb-focus-visible flex size-11 items-center justify-center rounded-full text-white transition-colors hover:bg-white/10"
            >
              <X size={24} />
            </Dialog.Close>
          </div>

          <div className="relative min-h-0 flex-1">
            <div className="h-full overflow-hidden" ref={emblaRef}>
              <div className="flex h-full">
                {images.map((image, i) => (
                  <div
                    key={image.id}
                    className="flex h-full min-w-0 flex-[0_0_100%] items-center justify-center px-4 pb-[max(2rem,env(safe-area-inset-bottom))] sm:px-20"
                  >
                    <RevealImage
                      src={image.url}
                      alt={hasMany ? `${alt} ${i + 1}` : alt}
                      className="max-h-full max-w-full select-none rounded-lg object-contain"
                      draggable={false}
                    />
                  </div>
                ))}
              </div>
            </div>

            {hasMany && (
              <>
                <button
                  type="button"
                  aria-label="Previous photo"
                  onClick={() => emblaApi?.scrollPrev()}
                  aria-disabled={index === 0}
                  className={mergeClassNames(NAV_BUTTON, 'left-4')}
                >
                  <ChevronLeft size={24} />
                </button>
                <button
                  type="button"
                  aria-label="Next photo"
                  onClick={() => emblaApi?.scrollNext()}
                  aria-disabled={index === images.length - 1}
                  className={mergeClassNames(NAV_BUTTON, 'right-4')}
                >
                  <ChevronRight size={24} />
                </button>
              </>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
